import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get, Post, Patch } from "libs/services";
import { ORDER } from "libs/config";
import { setLoading } from "./loader";
import { OrderItem, OrderResponse } from "src/common/types/response/orders";
import { openToast } from "./toast";
import { PostOrderRequest } from "src/common/types/request/postOrder";
import { processPaginatedItems } from "src/common/libs/utils/items";
import { BaseResponse } from "src/common/types/response/BasePaginationResponse";
import translate from "src/common/libs/locale";
import { CheckoutRequest } from "src/common/types/request/checkoutRequest";
import { CheckoutResponse } from "src/common/types/response/checkoutResponse";
import { reportLog } from "src/common/libs/utils/logs";

interface OrdersState {
  data?: BaseResponse<OrderItem>;
  loading: boolean;
  page: number;
  checkout: CheckoutResponse;
}

export const getOrders = createAsyncThunk(
  "order/getOrders",
  async ({ id, page = 1 }: { id: string; page: number }, thunkAPI) => {
    try {
      const response = await Get<BaseResponse<OrderItem>>(ORDER.get, {
        sort: "updated-desc",
        user: id,
        page,
      });
      return { data: response.data, page };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->getOrders",
        body: { id, page },
      });
      return null;
    }
  }
);

export const getOrderById = createAsyncThunk(
  "order/getOrderById",
  async ({ id }: { id: string }, thunkAPI) => {
    try {
      const response = await Get<BaseResponse<OrderItem>>(
        `${ORDER.singleOrder}/${id}`
      );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->getOrderById",
        body: { id },
      });
      return null;
    }
  }
);

export const getOrderStatusByTransactionId = createAsyncThunk(
  "order/getOrderStatusByTransactionId",
  async ({ id, userId }: { id: string; userId: string }, thunkAPI) => {
    try {
      const response = await Post<BaseResponse<OrderItem>>(
        `${ORDER.orderTransactionStatus}/${userId}`,
        {
          transactionId: id,
          agent: process.env.CLIENT,
        }
      );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->getOrderStatusByTransactionId",
        body: { id, userId },
      });
      return null;
    }
  }
);

export const postOrder = createAsyncThunk(
  "order/postOrder",
  async (body: PostOrderRequest, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const response = await Post(ORDER.post, body);
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->postOrder",
        body: { body },
      });
      thunkAPI.dispatch(
        openToast({ message: translate("unableToPlaceOrder") })
      );
      return { payload: { status: false } };
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const checkout = createAsyncThunk(
  "order/checkout",
  async (body: CheckoutRequest, thunkAPI): Promise<CheckoutResponse> => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const response = await Post<CheckoutResponse>(ORDER.checkout, body);
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->checkout",
        body: { body },
      });
      thunkAPI.dispatch(
        openToast({ message: translate("unableToPlaceOrder") })
      );
      return { status: false };
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const patchOrder = createAsyncThunk(
  "order/patchOrder",
  async ({ body }: { body }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const response = await Patch(ORDER.patch, {
        ...body,
        agent: process.env.CLIENT,
      });
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/orders->patchOrder",
        body: { body },
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      return { payload: { status: false } };
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const updateOrder = (body: { _id: string; status: number }) => {
  return Patch(ORDER.patch, body);
};

const initialState: OrdersState = {
  data: null,
  loading: false,
  page: 1,
  checkout: null,
};

export const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearData: () => initialState,
    clearAllOrders: () => initialState,
    updateStatus: (state, action: PayloadAction<OrderResponse[]>) => {
      const newPayload =
        state?.data?.payload.map((it) => {
          const presentItem = action.payload.find((pIt) => pIt._id === it._id);
          if (presentItem) {
            return { ...it, status: presentItem.status };
          }
          return it;
        }) || [];
      state.data = {
        ...state.data,
        payload: newPayload,
      };
    },
    updateOrderStatusById: (
      state,
      action: PayloadAction<{ id: string; status: number }>
    ) => {
      const newPayload =
        state?.data?.payload.map((it) => {
          if (it._id === action.payload.id) {
            return { ...it, status: action.payload.status };
          }
          return it;
        }) || [];
      state.data = {
        ...state.data,
        payload: newPayload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrders.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getOrders.fulfilled, (state, action) => {
      const { data, page } = action.payload || {};
      const filteredPayload = processPaginatedItems<any>(data, state.data);
      state.data = {
        ...state.data,
        ...filteredPayload,
      };
      state.page = page;
      state.loading = false;
    });
    builder.addCase(getOrders.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(checkout.pending, (state, action) => {
      // state.checkout = null;
    });
    builder.addCase(checkout.fulfilled, (state, action) => {
      state.checkout = action.payload;
    });
    builder.addCase(checkout.rejected, (state, action) => {
      state.checkout = null;
    });
  },
});

export const {
  clearData,
  clearAllOrders,
  updateStatus,
  updateOrderStatusById,
} = orderSlice.actions;

export default orderSlice.reducer;
